/**
 * The site search dialog.
 *
 * Opened by any Search Trigger on the page — the header's and the mobile
 * overlay's — and dismissed by the close button, the backdrop, or Escape.
 *
 * Follows the same shape as the speaker enquiry modal (src/js/speaker-inquire.js)
 * so the site has one way of doing dialogs, with the backdrop click that one
 * does not have.
 */

const FOCUSABLE = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled]):not([type="hidden"])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
].join( ',' );

/**
 * Wire the dialog to the triggers that open it.
 *
 * @param {HTMLElement} dialog The dialog element.
 */
function initDialog( dialog ) {
	const openers = document.querySelectorAll( '[data-dn-search-open]' );

	if ( ! openers.length ) {
		return;
	}

	const input = dialog.querySelector( 'input[type="search"]' );

	let lastFocused = null;

	const isOpen = () => ! dialog.hasAttribute( 'hidden' );

	const focusable = () =>
		Array.from( dialog.querySelectorAll( FOCUSABLE ) ).filter(
			( el ) => null !== el.offsetParent
		);

	function open() {
		if ( isOpen() ) {
			return;
		}

		lastFocused = document.activeElement;
		dialog.removeAttribute( 'hidden' );
		document.body.classList.add( 'dn-modal-open' );

		openers.forEach( ( button ) =>
			button.setAttribute( 'aria-expanded', 'true' )
		);

		// The field, not merely the first focusable thing: someone who opened a
		// search dialog is here to type, and the close button happens to come
		// first in the markup.
		if ( input ) {
			input.focus();
			input.select();
		}
	}

	function close( { restoreFocus = true } = {} ) {
		if ( ! isOpen() ) {
			return;
		}

		dialog.setAttribute( 'hidden', '' );
		document.body.classList.remove( 'dn-modal-open' );

		openers.forEach( ( button ) =>
			button.setAttribute( 'aria-expanded', 'false' )
		);

		// Back where they were, so a keyboard is not dropped at the top of the
		// document. Skipped when the dialog closed because focus already moved
		// somewhere deliberate.
		if ( restoreFocus && lastFocused && 'function' === typeof lastFocused.focus ) {
			lastFocused.focus();
		}
	}

	openers.forEach( ( button ) => {
		button.setAttribute( 'aria-expanded', 'false' );

		button.addEventListener( 'click', ( event ) => {
			event.preventDefault();
			open();
		} );
	} );

	// The close button and the backdrop both carry the attribute, so one
	// listener covers both and neither needs to know about the other.
	dialog.addEventListener( 'click', ( event ) => {
		if ( event.target.closest( '[data-dn-search-close]' ) ) {
			event.preventDefault();
			close();
		}
	} );

	// Submitting with an empty field would send the visitor to a results page
	// for nothing at all. Keep them here and say so by putting focus back.
	dialog.addEventListener( 'submit', ( event ) => {
		if ( input && '' === input.value.trim() ) {
			event.preventDefault();
			input.focus();
		}
	} );

	document.addEventListener( 'keydown', ( event ) => {
		if ( ! isOpen() ) {
			return;
		}

		if ( 'Escape' === event.key ) {
			event.preventDefault();
			close();

			return;
		}

		if ( 'Tab' !== event.key ) {
			return;
		}

		// Focus is trapped, so Tab cannot wander onto the page the dialog is
		// covering — which a screen reader has already been told is inert by
		// aria-modal, and which would otherwise disagree with it.
		const items = focusable();

		if ( ! items.length ) {
			return;
		}

		const first = items[ 0 ];
		const last = items[ items.length - 1 ];

		if ( event.shiftKey && document.activeElement === first ) {
			event.preventDefault();
			last.focus();
		} else if ( ! event.shiftKey && document.activeElement === last ) {
			event.preventDefault();
			first.focus();
		}
	} );
}

/**
 * Start the dialog.
 */
function init() {
	const dialog = document.getElementById( 'dn-search-dialog' );

	if ( dialog ) {
		initDialog( dialog );
	}
}

if ( 'loading' === document.readyState ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}
